import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useViewer } from '@vimeo/viewer';

import type { PicoX } from './picox';
import { createPicoX } from './picox';
import { extractSafeViewerInfo } from './utils';

// NOT A SECRET
const IDENTIFICATION_TOKEN = 'c10f1887-7c05-4a9a-9a60-c307825f0f34';

const PicoContext = createContext<PicoX | null>(null);
export const usePico = () => useContext(PicoContext);

/**
 * User and session identifiers for PicoX analytics.
 * These are stable identifiers that identify the user/session.
 */
export type PicoIdentifiers = {
  /** User ID from Vimeo */
  user_id: string | null;
  /** Visitor unique ID (marketing/analytics cookie) */
  vuid: string | null;
  /** Team ID if user is part of a team */
  team_id?: string | null;
  /** Team owner's user ID */
  team_owner_id?: string | null;
  /** Enterprise organization ID */
  organization_id?: string | null;
};

/**
 * Contextual data for PicoX analytics.
 * These are values that describe the user's current state and may change.
 */
export type PicoContextData = {
  /** Account type: basic, plus, pro, business, enterprise, etc. */
  account_type: string | null;
  /** Whether user is part of a team */
  is_team_user: boolean | null;
  /** Whether user is currently in a free trial */
  is_free_trial: boolean | null;
  /** User's country code */
  country: string | null;
  /** Whether user is on a mobile device */
  is_mobile: boolean | null;
};

type PicoProviderProps = {
  children: React.ReactNode;
  // TODO: Remove this once all the other _app.tsx files are updated to use the new identifiers provider
  identifiers?: PicoIdentifiers | null;
  isDevelopment?: boolean;
  syncIntervalMilliseconds?: number;
};

function defaultIsDevelopment(): boolean {
  // Local and CI environments
  return (
    window.location.hostname === 'vimeo.dev' || window.location.hostname.endsWith('.vimeows.com')
  );
}

export const PicoProvider: React.FC<PicoProviderProps> = ({
  children,
  syncIntervalMilliseconds = 1000,
  isDevelopment,
}) => {
  const viewer = useViewer();

  const {
    user_id,
    vuid,
    team_id,
    team_owner_id,
    organization_id,
    account_type,
    is_team_user,
    is_free_trial,
    country,
    is_mobile,
  } = extractSafeViewerInfo(viewer);

  const hasViewer = viewer !== null && viewer !== undefined;

  const computedIdentifiers = useMemo(
    (): PicoIdentifiers | null =>
      hasViewer
        ? {
            user_id,
            vuid,
            team_id,
            team_owner_id,
            organization_id,
          }
        : null,
    [user_id, vuid, team_id, team_owner_id, organization_id, hasViewer],
  );

  const computedContext = useMemo(
    (): PicoContextData | null =>
      hasViewer
        ? {
            account_type,
            is_team_user,
            is_free_trial,
            country,
            is_mobile,
          }
        : null,
    [account_type, is_team_user, is_free_trial, country, is_mobile, hasViewer],
  );

  // These refs always hold the latest values so that the getter closures passed
  // to createPicoX (which are called at track-time, not at init-time) return
  // up-to-date data without requiring a new PicoX instance.
  const identifiersRef = useRef<PicoIdentifiers | null>(computedIdentifiers);
  const contextRef = useRef<PicoContextData | null>(computedContext);
  identifiersRef.current = computedIdentifiers;
  contextRef.current = computedContext;

  const [picoX, setPicoX] = useState<PicoX | null>(null);

  // Initialize a single PicoX instance when the viewer first becomes available.
  // User/context changes are handled transparently through the refs above.
  useEffect(() => {
    if (!hasViewer) {
      return;
    }

    const additionalIdentifiers = () => ({ ...(identifiersRef.current ?? {}) });
    const additionalContext = () => {
      let page: string | null = null;
      if (typeof window !== 'undefined') {
        page = window.location.pathname;
      }
      return {
        page,
        ...(contextRef.current ?? {}),
      };
    };

    // To avoid AdBlockers, we send events to a random subdomain of vimeo.com
    // and use Fastly to re-route them to PicoX.
    // IMPORTANT: The endpoint has to be blocked in robots.txt:
    // https://github.com/vimeows/vimeo/blob/8dd06dd742eb6dfe0ac2ee2977a531933545409d/views_v6/sitemap/robots_txt.phtml
    // Otherwise, even it's a POST request, Google will still discover it and try to index. We would end up wasting crawl budget.
    const development = isDevelopment || defaultIsDevelopment();
    const endpoint =
      // TODO[film]: Find out why targeting vimeo-staging gives CORS errors
      // Temporarily, we target vimeo.com for both development and production.
      (development ? 'https://vimeo.com/flarepoint' : 'https://vimeo.com/flarepoint') +
      `/${getRandomString(8)}`;

    const loadPicoXClient = async () => {
      try {
        const instance = await createPicoX({
          additionalIdentifiers,
          additionalContext,
          cookieDomain: `.${window.location.hostname.split('.').slice(-2).join('.')}`,
          identificationToken: IDENTIFICATION_TOKEN,
          isDevelopment: development,
          syncIntervalMilliseconds,
          endpoint,
        });
        setPicoX(instance);
      } catch (error) {
        console.error('Error initializing PicoX client', error);
      }
    };

    void loadPicoXClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasViewer]);

  return <PicoContext.Provider value={picoX}>{children}</PicoContext.Provider>;
};

function getRandomString(length: number): string {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  return result;
}
